import React, { useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FiExternalLink, FiHelpCircle, FiX } from 'react-icons/fi';
import { GuideBody } from './GuideBody';
import { GuideHeader } from './components/GuideHeader';
import { GuidePager } from './components/GuidePager';
import { useHelp } from './HelpProvider';
import { getGuide } from './registry';
import { useGuides } from './useGuides';
import { useGuideText } from './useGuideText';

export const HelpDrawer: React.FC = () => {
  const { t } = useTranslation('HelpGuide');
  const { isOpen, guideId, closeHelp, showGuide } = useHelp();
  const { guides } = useGuides();
  const { title } = useGuideText();
  const location = useLocation();
  const guide = getGuide(guideId);
  const closeRef = useRef<HTMLButtonElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);

  // Switching guides re-renders in place; start each guide at its top.
  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
  }, [guideId]);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeHelp();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, closeHelp]);

  useEffect(() => {
    if (isOpen) closeRef.current?.focus();
  }, [isOpen]);

  // The full Help Center supersedes the drawer.
  useEffect(() => {
    if (location.pathname.startsWith('/help')) closeHelp();
  }, [location.pathname, closeHelp]);

  if (!isOpen || !guide) return null;

  // Keep the active guide selectable even when it is hidden from this user.
  const options = guides.some((g) => g.id === guide.id)
    ? guides
    : [guide, ...guides];

  return (
    <aside
      role="dialog"
      aria-modal="false"
      aria-labelledby="help-drawer-title"
      className="fixed inset-y-0 right-0 z-[150] flex w-[min(96vw,720px)] flex-col border-l border-gray-200 bg-white shadow-2xl dark:border-gray-700 dark:bg-gray-900"
    >
      <div className="flex shrink-0 items-center justify-between gap-2 border-b border-gray-200 px-6 py-3 dark:border-gray-700">
        <div className="flex min-w-0 items-center gap-2">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400">
            <FiHelpCircle size={16} />
          </span>
          <h2
            id="help-drawer-title"
            className="truncate text-sm font-bold text-gray-800 dark:text-gray-100"
          >
            {t('drawer.title')}
          </h2>
        </div>
        <div className="flex items-center gap-1">
          <Link
            to={`/help/${guide.id}`}
            className="flex h-8 items-center gap-1.5 rounded-lg px-2 text-xs font-medium text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-gray-800"
          >
            <FiExternalLink size={14} />
            {t('drawer.openCenter')}
          </Link>
          <button
            ref={closeRef}
            type="button"
            onClick={closeHelp}
            aria-label={t('drawer.close')}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:text-gray-500 dark:hover:bg-gray-800 dark:hover:text-gray-300"
          >
            <FiX size={16} />
          </button>
        </div>
      </div>

      <div className="shrink-0 border-b border-gray-200 px-6 py-2 dark:border-gray-700">
        <select
          aria-label={t('drawer.selectGuide')}
          value={guide.id}
          onChange={(e) => showGuide(e.target.value)}
          className="w-full rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-800 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
        >
          {options.map((g) => (
            <option key={g.id} value={g.id}>
              {title(g.id)}
            </option>
          ))}
        </select>
      </div>

      <div ref={bodyRef} className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
        <GuideHeader guide={guide} compact level={3} />
        <GuideBody guideId={guide.id} onNavigateGuide={showGuide} />
        <GuidePager guides={guides} activeId={guide.id} onSelect={showGuide} />
      </div>
    </aside>
  );
};
